import { useCallback, useEffect, useRef, useState } from 'react';
import AnchoredPop from './AnchoredPop.tsx';
import PersonAvatar from './PersonAvatar.tsx';
import { useDismissMenu } from '../lib/useDismissMenu.ts';
import { currentUser, forgetUser } from '../lib/me.ts';
import type { Person } from '../../shared/organisation.ts';

/**
 * Who you are signed in as, at the right of the app header, opening onto Sign
 * out.
 *
 * It replaces the "Sign out" text link that every surface carried, which said
 * what you could do and never who you were.
 *
 * The face is `PersonAvatar`, shared with the People list so the two never
 * drift.
 */
export default function AccountMenu({ inSheet, onSignOut }: { inSheet: boolean; onSignOut: () => void }) {
  const [person, setPerson] = useState<Person | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    currentUser().then((me) => {
      if (live) setPerson(me?.person ?? null);
    });
    return () => {
      live = false;
    };
  }, []);

  // Both refs, because the panel is portalled out of this subtree when floating
  // and a click on one of its own rows would otherwise read as a click away.
  const close = useCallback(() => setOpen(false), []);
  useDismissMenu([rootRef, panelRef], close, open);

  function signOut() {
    setOpen(false);
    forgetUser();
    onSignOut();
  }

  // In the sheet this is one row among the others, and a dropdown inside a
  // bottom sheet would be a menu inside a menu. The identity is already at the
  // top of the sheet's own list, so the row is just the verb.
  if (inSheet) {
    return (
      <button type="button" className="app-sheet__row" onClick={signOut}>
        <span className="app-sheet__row-label">
          Sign out
          {person ? <span className="app-sheet__row-hint">{person.email || person.name}</span> : null}
        </span>
      </button>
    );
  }

  return (
    <div className="account-menu" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="account-menu__button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={person ? `Account: ${person.name}` : 'Account'}
        title={person ? person.name : 'Account'}
      >
        <PersonAvatar person={person} />
      </button>
      {open && (
        <AnchoredPop anchor={buttonRef} popRef={panelRef} align="right" gap={6} className="menu account-menu__panel">
          <div className="account-menu__who">
            <PersonAvatar person={person} />
            <span className="account-menu__names">
              <span className="account-menu__name">{person ? person.name : 'Signed in'}</span>
              {/* A session always names somebody now, so an empty person here
                  means the lookup failed rather than that nobody is behind it. */}
              <span className="account-menu__email muted small">{person?.email ?? 'account not found'}</span>
            </span>
          </div>
          <hr className="menu__sep" />
          <button type="button" className="menu__item" role="menuitem" onClick={signOut}>
            Sign out
          </button>
        </AnchoredPop>
      )}
    </div>
  );
}
